import {ChangeDetectorRef, Component, EventEmitter, Input, Output, SimpleChanges} from '@angular/core';
import {bsMonthDaysByYear, bsMonthNames, toNepaliNumber} from "../nepali-calendar-data";
import {FormsModule} from '@angular/forms';


export interface BSDate {
  y: number;
  m: number;
  d: number;
}

export function gregorianToBs(gregDate: Date) {
  const refG = new Date(1943, 3, 14);
  const refBS = {y: 2000, m: 0, d: 1};

  const targetG = new Date(
    gregDate.getFullYear(),
    gregDate.getMonth(),
    gregDate.getDate()
  );
  let diff = Math.floor(
    (targetG.getTime() - refG.getTime()) / (24 * 60 * 60 * 1000)
  );

  if (diff > 0) diff++;

  let year = refBS.y,
    month = refBS.m,
    day = refBS.d;

  while (diff !== 0) {
    const step = Math.sign(diff);
    day += step;

    if (day > (bsMonthDaysByYear[year]?.[month] || 30)) {
      day = 1;
      month++;
      if (month > 11) {
        month = 0;
        year++;
      }
    }

    if (day < 1) {
      month--;
      if (month < 0) {
        month = 11;
        year--;
      }
      day = bsMonthDaysByYear[year]?.[month] || 30;
    }

    diff -= step;
  }

  return {y: year, m: month, d: day};
}

export function bsToGregorian(bsDate: {
  y: number;
  m: number;
  d: number;
}): Date {
  const refG = new Date(1943, 3, 14);
  const refBS = {y: 2000, m: 0, d: 1};

  let diff = 0;

  if (
    bsDate.y > refBS.y ||
    (bsDate.y === refBS.y && bsDate.m > refBS.m) ||
    (bsDate.y === refBS.y && bsDate.m === refBS.m && bsDate.d >= refBS.d)
  ) {
    for (let y = refBS.y; y < bsDate.y; y++) {
      diff += bsMonthDaysByYear[y]?.reduce((a, b) => a + b, 0) || 0;
    }
    for (let m = 0; m < bsDate.m; m++) {
      diff += bsMonthDaysByYear[bsDate.y]?.[m] || 30;
    }
    diff += bsDate.d - refBS.d + 1;
  } else {
    for (let y = bsDate.y; y < refBS.y; y++) {
      diff -= bsMonthDaysByYear[y]?.reduce((a, b) => a + b, 0) || 0;
    }
    for (let m = 11; m > bsDate.m; m--) {
      diff -= bsMonthDaysByYear[bsDate.y]?.[m] || 30;
    }
    diff -= refBS.d - bsDate.d + 1;
  }

  return new Date(refG.getTime() + diff * 24 * 60 * 60 * 1000);
}

@Component({
  selector: 'app-nepali-datepicker',
  templateUrl: './nepali-datepicker.component.html',
  imports: [FormsModule],
  standalone: true,
  styleUrls: ['./nepali-datepicker.component.scss']
})
export class NepaliDatepickerComponent {
  @Input() initialDate: BSDate | null = null;
  @Input() minDate?: BSDate;
  @Input() maxDate?: BSDate;

  @Output() dateSelected = new EventEmitter<BSDate>();

  viewYear = 2000;
  viewMonth = 0;
  selectedDate: BSDate | null = null;
  nepaliWeekdays = ['आइ', 'सोम', 'मंगल', 'बुध', 'बिही', 'शुक्र', 'शनि'];

  bsMonthNames = bsMonthNames;

  // monthOptions = Array.from({ length: 12 }, (_, i) => i);
  get monthOptionsFiltered(): number[] {
    let months = Array.from({length: 12}, (_, i) => i);

    if (this.minDate && this.viewYear === this.minDate.y) {
      months = months.filter(m => m >= this.minDate!.m);
    }

    if (this.maxDate && this.viewYear === this.maxDate.y) {
      months = months.filter(m => m <= this.maxDate!.m);
    }

    return months;
  }

  // yearOptions = Object.keys(bsMonthDaysByYear)
  //   .map(Number)
  //   .sort((a, b) => a - b);
  get yearOptions(): number[] {
    let years = Object.keys(bsMonthDaysByYear)
      .map(Number)
      .sort((a, b) => a - b);

    if (this.minDate) {
      years = years.filter(y => y >= this.minDate!.y);
    }

    if (this.maxDate) {
      years = years.filter(y => y <= this.maxDate!.y);
    }

    return years;
  }


  dowCache: Record<string, number> = {};
  convertedADDate: string | null = null;

  constructor(private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.setViewToInitialDate();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialDate'] && this.initialDate) {
      this.setViewToInitialDate();
    }
  }

  setViewToInitialDate() {
    if (this.initialDate) {
      this.selectedDate = {...this.initialDate};
      this.viewYear = this.initialDate.y;
      this.viewMonth = this.initialDate.m;
    } else {
      const today = new Date();
      const bsToday = gregorianToBs(today);
      this.selectedDate = {y: bsToday.y, m: bsToday.m, d: bsToday.d};
      this.viewYear = bsToday.y;
      this.viewMonth = bsToday.m;
    }
  }

  get daysInMonth(): number {
    return bsMonthDaysByYear[this.viewYear]?.[this.viewMonth] || 30;
  }

  get days(): number[] {
    return Array.from({length: this.daysInMonth}, (_, i) => i + 1);
  }

  get monthName(): string {
    return bsMonthNames[this.viewMonth];
  }

  getFirstDayOfWeek(): number {
    const cacheKey = `${this.viewYear}-${this.viewMonth}`;
    if (this.dowCache[cacheKey] !== undefined) {
      return this.dowCache[cacheKey];
    }

    let totalDays = 0;
    for (let year = 2000; year < this.viewYear; year++) {
      totalDays += bsMonthDaysByYear[year]?.reduce((a, b) => a + b, 0) || 0;
    }

    for (let month = 0; month < this.viewMonth; month++) {
      totalDays += bsMonthDaysByYear[this.viewYear]?.[month] || 30;
    }

    const startingDay2000 = 3;
    const firstDayOfWeek = (startingDay2000 + totalDays) % 7;

    this.dowCache[cacheKey] = firstDayOfWeek;
    return firstDayOfWeek;
  }

  getSelectedWeekday(): string | null {
    if (!this.selectedDate) return null;
    if (
      this.selectedDate.y !== this.viewYear ||
      this.selectedDate.m !== this.viewMonth
    )
      return null;
    const firstDay = this.getFirstDayOfWeek();
    const weekdayIndex = (firstDay + this.selectedDate.d - 1) % 7;
    return this.nepaliWeekdays[weekdayIndex];
  }

  prevMonth() {
    if (this.viewMonth === 0) {
      this.viewMonth = 11;
      this.viewYear--;
    } else {
      this.viewMonth--;
    }
    this.dowCache = {};
  }

  nextMonth() {
    if (this.viewMonth === 11) {
      this.viewMonth = 0;
      this.viewYear++;
    } else {
      this.viewMonth++;
    }
    this.dowCache = {};
  }

  selectDay(day: number) {
    // NEW: Check if date is disabled before selecting
    if (this.isDateDisabled(day)) {
      return; // Don't select disabled dates
    }

    this.selectedDate = {y: this.viewYear, m: this.viewMonth, d: day};
    this.dateSelected.emit({y: this.viewYear, m: this.viewMonth, d: day});
    const adDate = bsToGregorian(this.selectedDate);
    this.convertedADDate = adDate.toISOString().split('T')[0];
  }

  // NEW: Check if a BS date should be disabled
  isDateDisabled(day: number): boolean {
    const currentBSDate: BSDate = {y: this.viewYear, m: this.viewMonth, d: day};

    if (this.minDate) {
      if (this.compareBSDates(currentBSDate, this.minDate) < 0) {
        return true; // Disable dates before minDate
      }
    }

    if (this.maxDate) {
      if (this.compareBSDates(currentBSDate, this.maxDate) > 0) {
        return true; // Disable dates after maxDate
      }
    }

    return false;
  }

  // NEW: Helper to compare two BS dates
  // Returns: -1 if date1 < date2, 0 if equal, 1 if date1 > date2
  private compareBSDates(date1: BSDate, date2: BSDate): number {
    if (date1.y !== date2.y) return date1.y < date2.y ? -1 : 1;
    if (date1.m !== date2.m) return date1.m < date2.m ? -1 : 1;
    if (date1.d !== date2.d) return date1.d < date2.d ? -1 : 1;
    return 0;
  }

  isSaturday(day: number): boolean {
    const firstDay = this.getFirstDayOfWeek();
    const weekdayIndex = (firstDay + day - 1) % 7;
    return weekdayIndex === 6; // Saturday is index 6
  }

  toNepaliNumber = toNepaliNumber;

  onMonthChange() {
    this.viewMonth = Number(this.viewMonth);
    this.dowCache = {};
    this.cdr.detectChanges();
  }

  onYearChange() {
    this.viewYear = Number(this.viewYear);
    this.dowCache = {};
    this.cdr.detectChanges();
  }

  isSelected(day: number): boolean {
    return !!(
      this.selectedDate &&
      this.selectedDate.y === this.viewYear &&
      this.selectedDate.m === this.viewMonth &&
      this.selectedDate.d === day
    );
  }

  // NEW: Methods for action buttons
  goToToday() {
    const today = gregorianToBs(new Date());
    this.viewYear = today.y;
    this.viewMonth = today.m;

    if (!this.isDateDisabled(today.d)) {
      this.selectDay(today.d);
    }
  }

  clearSelection() {
    this.selectedDate = null;
    this.convertedADDate = null;
    this.dateSelected.emit(null as any);
  }
}
